<script setup>
import { computed, onMounted, ref } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useUserStore } from '@/stores/user'
import HeaderPanel from '@/components/HeaderPanel.vue'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Select from 'primevue/select'
import { wisdomAdminApi } from '@/services/wisdomAdminApi'

defineProps({
  hideHeader: { type: Boolean, default: false }
})

const userStore = useUserStore()
const toast = useToast()

const loading = ref(false)
const saving = ref(false)
const coverUploading = ref(false)
const items = ref([])
const total = ref(0)
const page = ref(1)
const limit = ref(50)
const first = computed({
  get: () => (page.value - 1) * limit.value,
  set: (value) => {
    page.value = Math.floor(value / (limit.value || 1)) + 1
  },
})

const meta = ref({
  themes: [],
  tags: [],
  total_lessons: 0,
  total_materials: 0,
  total_glossary: 0,
})

const q = ref('')
const theme = ref(null)
const tag = ref(null)
const gap = ref('')
const gapCounts = ref({})

const gapChips = [
  { key: 'no_cover', label: 'Без обложки' },
  { key: 'no_description', label: 'Без описания' },
  { key: 'no_video', label: 'Без видео' },
  { key: 'no_glossary', label: 'Без глоссария' },
  { key: 'no_date', label: 'Без даты' },
  { key: 'no_materials', label: 'Без материалов' },
  { key: 'no_transcript', label: 'Без транскрибации' },
  { key: 'no_link', label: 'Без ссылки' },
]

const editorVisible = ref(false)
const editingId = ref(null)
const form = ref(emptyForm())
const coverInput = ref(null)

const themeOptions = computed(() => meta.value.themes.map((row) => ({
  label: `${row.theme || 'без темы'} (${row.lessons})`,
  value: row.theme,
})))

const tagOptions = computed(() => {
  const rows = meta.value.tags.filter((row) => !theme.value || row.theme === theme.value)
  return rows.map((row) => ({
    label: `${row.tag || 'без тега'} (${row.lessons})`,
    value: row.tag,
  }))
})

function emptyForm() {
  return {
    theme: '',
    tag: '',
    tag_description: '',
    title: '',
    lesson_title: '',
    lesson_date: '',
    cover_url: '',
    description: '',
    source_url: '',
    materials: [],
    glossary: [],
  }
}

function apiMessage(error) {
  return error.response?.data?.error || error.response?.data?.message || error.message
}

function text(value) {
  return value == null ? '' : String(value)
}

function toDateInput(value) {
  if (!value) return ''
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : ''
}

function formatDate(value) {
  const iso = toDateInput(value)
  if (!iso) return ''
  const [year, month, day] = iso.split('-')
  return `${day}.${month}.${year}`
}

function plural(n, one, few, many) {
  const abs = Math.abs(Number(n)) % 100
  const last = abs % 10
  if (abs > 10 && abs < 20) return many
  if (last > 1 && last < 5) return few
  if (last === 1) return one
  return many
}

function filterParams() {
  return {
    q: q.value.trim(),
    theme: theme.value || '',
    tag: tag.value || '',
  }
}

function listParams() {
  const params = {
    ...filterParams(),
    limit: limit.value,
    offset: (page.value - 1) * limit.value,
  }
  if (gap.value) params.gap = gap.value
  return params
}

async function loadMeta() {
  const { data } = await wisdomAdminApi.meta()
  meta.value = {
    themes: data.themes || [],
    tags: data.tags || [],
    total_lessons: data.total_lessons || 0,
    total_materials: data.total_materials || 0,
    total_glossary: data.total_glossary || 0,
  }
}

async function loadList() {
  loading.value = true
  try {
    const { data } = await wisdomAdminApi.list(listParams())
    items.value = (data.items || []).map((row) => ({
      ...row,
      is_hidden: row.is_hidden === true,
    }))
    total.value = data.total || 0
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Список', detail: apiMessage(error), life: 4000 })
  } finally {
    loading.value = false
  }
}

async function loadGaps() {
  try {
    const { data } = await wisdomAdminApi.gaps(filterParams())
    const counts = {}
    for (const row of data.gaps || []) counts[row.key] = Number(row.count) || 0
    gapCounts.value = counts
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Фильтры', detail: apiMessage(error), life: 4000 })
  }
}

function reloadFromStart() {
  page.value = 1
  loadList()
}

function onFiltersChanged() {
  page.value = 1
  loadList()
  loadGaps()
}

function selectGap(key) {
  const next = key && gap.value === key ? '' : key
  if (next === gap.value) return
  gap.value = next
  reloadFromStart()
}

function onPage(event) {
  const rowsChanged = event.rows !== limit.value
  if (rowsChanged) {
    limit.value = event.rows
    page.value = 1
  } else {
    page.value = event.page + 1
  }
  loadList()
}

async function refreshAll() {
  try {
    await loadMeta()
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Счётчики', detail: apiMessage(error), life: 4000 })
  }
  await Promise.all([loadList(), loadGaps()])
}

function fillForm(payload) {
  const lesson = payload.lesson || {}
  form.value = {
    theme: text(lesson.theme),
    tag: text(lesson.tag),
    tag_description: text(lesson.tag_description),
    title: text(lesson.title),
    lesson_title: text(lesson.lesson_title),
    lesson_date: toDateInput(lesson.lesson_date),
    cover_url: text(lesson.cover_url),
    description: text(lesson.description),
    source_url: text(lesson.source_url),
    materials: (payload.materials || []).map((row) => ({
      kind: text(row.kind),
      title: text(row.title),
      url: text(row.url),
    })),
    glossary: (payload.glossary || []).map((row) => ({
      term: text(row.term),
      definition: text(row.definition),
    })),
  }
}

async function openLesson(item) {
  editingId.value = item.id
  form.value = emptyForm()
  editorVisible.value = true
  try {
    const { data } = await wisdomAdminApi.get(item.id)
    fillForm(data)
  } catch (error) {
    editorVisible.value = false
    toast.add({ severity: 'error', summary: 'Урок', detail: apiMessage(error), life: 4000 })
  }
}

function moveRow(listName, index, dir) {
  const next = index + dir
  const list = form.value[listName]
  if (next < 0 || next >= list.length) return
  const copy = list.slice()
  const [row] = copy.splice(index, 1)
  copy.splice(next, 0, row)
  form.value[listName] = copy
}

function addMaterial() {
  form.value.materials.push({ kind: '', title: '', url: '' })
}

function addTerm() {
  form.value.glossary.push({ term: '', definition: '' })
}

async function saveLesson() {
  if (!editingId.value) return
  saving.value = true
  try {
    const payload = {
      theme: form.value.theme,
      tag: form.value.tag,
      tag_description: form.value.tag_description,
      title: form.value.title,
      lesson_title: form.value.lesson_title,
      lesson_date: form.value.lesson_date,
      cover_url: form.value.cover_url,
      description: form.value.description,
      materials: form.value.materials.map((row, index) => ({
        kind: row.kind,
        title: row.title,
        url: row.url,
        position: index + 1,
      })),
      glossary: form.value.glossary.map((row, index) => ({
        term: row.term,
        definition: row.definition,
        position: index + 1,
      })),
    }
    const { data } = await wisdomAdminApi.update(editingId.value, payload)
    fillForm(data)
    toast.add({ severity: 'success', summary: 'Урок', detail: 'Сохранено', life: 2500 })
    await refreshAll()
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Сохранение', detail: apiMessage(error), life: 4000 })
  } finally {
    saving.value = false
  }
}

function onRowClick(event) {
  if (event?.data) openLesson(event.data)
}

async function toggleHidden(item) {
  const next = item.is_hidden !== true
  try {
    const { data } = await wisdomAdminApi.update(item.id, { is_hidden: next })
    item.is_hidden = data?.lesson?.is_hidden === true
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Скрытие', detail: apiMessage(error), life: 4000 })
  }
}

async function onCoverFile(event) {
  const file = event.target.files && event.target.files[0]
  event.target.value = ''
  if (!file || !editingId.value) return
  coverUploading.value = true
  try {
    const { data } = await wisdomAdminApi.uploadCover(editingId.value, file)
    form.value.cover_url = data.cover_url || ''
    toast.add({ severity: 'success', summary: 'Обложка', detail: 'Загружена', life: 2500 })
    await refreshAll()
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Обложка', detail: apiMessage(error), life: 4000 })
  } finally {
    coverUploading.value = false
  }
}

function onThemeChange() {
  if (tag.value && !tagOptions.value.some((option) => option.value === tag.value)) tag.value = null
  onFiltersChanged()
}

onMounted(async () => {
  await userStore.checkAuth()
  await refreshAll()
})
</script>

<template>
  <ToastVue />
  <HeaderPanel v-if="!hideHeader" />
  <div class="wisdom-page">
    <header class="wisdom-header">
      <div>
        <h1>База мудрости</h1>
        <p class="subtitle">
          {{ meta.total_lessons }} уроков / {{ meta.total_materials }} материалов / {{ meta.total_glossary }} терминов
        </p>
      </div>
      <slot name="header-actions" />
    </header>

    <section class="wisdom-filters">
      <div class="filters-main">
        <div class="field grow">
          <label>Поиск</label>
          <InputText
            v-model="q"
            placeholder="Название и описание"
            @keyup.enter="onFiltersChanged"
          />
        </div>
        <div class="field">
          <label>Тема</label>
          <Select
            v-model="theme"
            :options="themeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Все"
            showClear
            @update:modelValue="onThemeChange"
          />
        </div>
        <div class="field">
          <label>Тег</label>
          <Select
            v-model="tag"
            :options="tagOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Все"
            showClear
            :disabled="!theme"
            @update:modelValue="onFiltersChanged"
          />
        </div>
      </div>
    </section>

    <section class="wisdom-table-card">
      <div class="table-header">
        <h2>Уроки</h2>
        <div class="table-actions">
          <Button
            label="Обновить"
            icon="pi pi-refresh"
            severity="secondary"
            :loading="loading"
            @click="refreshAll"
          />
        </div>
      </div>

      <div class="gap-filters" role="group" aria-label="Неполнота">
        <button
          type="button"
          class="gap-chip"
          :class="{ active: !gap }"
          @click="selectGap('')"
        >
          Все
        </button>
        <button
          v-for="item in gapChips"
          :key="item.key"
          type="button"
          class="gap-chip"
          :class="{ active: gap === item.key }"
          @click="selectGap(item.key)"
        >
          {{ item.label }}
          <span class="gap-count">{{ gapCounts[item.key] ?? 0 }}</span>
        </button>
      </div>

      <DataTable
        :value="items"
        dataKey="id"
        :loading="loading"
        size="small"
        class="wisdom-table"
        @row-click="onRowClick"
      >
        <ColumnTable header="" :style="{ width: '3.5rem' }">
          <template #body="{ data }">
            <div class="row-actions">
              <Button
                type="button"
                :icon="data.is_hidden ? 'pi pi-eye-slash' : 'pi pi-eye'"
                :title="data.is_hidden ? 'Показать урок' : 'Скрыть урок'"
                text
                rounded
                :severity="data.is_hidden ? 'warn' : 'secondary'"
                @click.stop="toggleHidden(data)"
              />
            </div>
          </template>
        </ColumnTable>

        <ColumnTable header="Обложка" :style="{ width: '6rem' }">
          <template #body="{ data }">
            <img v-if="data.cover_url" :src="data.cover_url" class="cover-thumb" alt="" />
            <span v-else class="cover-empty">—</span>
          </template>
        </ColumnTable>

        <ColumnTable field="title" header="Название">
          <template #body="{ data }">
            <div class="title-cell">
              <span>{{ data.title || 'без названия' }}</span>
              <small v-if="data.lesson_title" class="topic-sub">{{ data.lesson_title }}</small>
            </div>
          </template>
        </ColumnTable>

        <ColumnTable field="theme" header="Тема" />
        <ColumnTable field="tag" header="Тег" />

        <ColumnTable field="lesson_date" header="Дата" :style="{ width: '7rem' }">
          <template #body="{ data }">
            {{ formatDate(data.lesson_date) }}
          </template>
        </ColumnTable>

        <ColumnTable header="Материалы" :style="{ width: '8rem' }">
          <template #body="{ data }">
            {{ data.materials_count }} {{ plural(data.materials_count, 'материал', 'материала', 'материалов') }}
          </template>
        </ColumnTable>

        <ColumnTable header="Глоссарий" :style="{ width: '8rem' }">
          <template #body="{ data }">
            {{ data.glossary_count }} {{ plural(data.glossary_count, 'термин', 'термина', 'терминов') }}
          </template>
        </ColumnTable>

        <template #empty>
          <div class="empty-state">
            <p>Ничего не найдено</p>
          </div>
        </template>
      </DataTable>

      <Paginator
        v-model:first="first"
        :rows="limit"
        :totalRecords="total"
        :rowsPerPageOptions="[50, 100, 200]"
        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
        currentPageReportTemplate="{first}–{last} из {totalRecords}"
        class="wisdom-paginator"
        @page="onPage"
      />
    </section>

    <Dialog
      v-model:visible="editorVisible"
      header="Урок"
      modal
      :style="{ width: 'min(960px, 96vw)' }"
    >
      <div class="form-grid">
        <label>Тема</label>
        <InputText v-model="form.theme" class="w-full" />

        <label>Тег</label>
        <InputText v-model="form.tag" class="w-full" />

        <label>Описание к тегу</label>
        <Textarea v-model="form.tag_description" rows="2" class="w-full" autoResize />

        <label>Название эфира</label>
        <InputText v-model="form.title" class="w-full" />

        <label>Заголовок урока</label>
        <InputText v-model="form.lesson_title" class="w-full" />

        <label>Дата</label>
        <InputText v-model="form.lesson_date" type="date" class="w-full" />

        <label>Ссылка на урок</label>
        <InputText :modelValue="form.source_url" readonly class="w-full" />

        <label>Описание</label>
        <Textarea v-model="form.description" rows="8" class="w-full" autoResize />

        <label>Обложка</label>
        <div class="cover-edit">
          <img v-if="form.cover_url" :src="form.cover_url" alt="" class="cover-preview" />
          <InputText v-model="form.cover_url" class="w-full" placeholder="https://" />
          <input
            ref="coverInput"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            class="file-input"
            @change="onCoverFile"
          />
          <Button
            label="Загрузить файл"
            icon="pi pi-upload"
            severity="secondary"
            :loading="coverUploading"
            @click="coverInput && coverInput.click()"
          />
        </div>

        <div class="section-head">
          <h3>Материалы</h3>
          <Button label="Добавить" icon="pi pi-plus" size="small" text @click="addMaterial" />
        </div>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Тип</th>
                <th>Название</th>
                <th>Ссылка</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in form.materials" :key="index">
                <td><InputText v-model="row.kind" class="w-full" /></td>
                <td><InputText v-model="row.title" class="w-full" /></td>
                <td><InputText v-model="row.url" class="w-full" /></td>
                <td class="row-actions">
                  <Button icon="pi pi-arrow-up" text size="small" :disabled="index === 0" @click="moveRow('materials', index, -1)" />
                  <Button icon="pi pi-arrow-down" text size="small" :disabled="index === form.materials.length - 1" @click="moveRow('materials', index, 1)" />
                  <Button icon="pi pi-trash" text size="small" severity="danger" @click="form.materials.splice(index, 1)" />
                </td>
              </tr>
              <tr v-if="!form.materials.length">
                <td colspan="4" class="empty-cell">Материалов нет</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="section-head">
          <h3>Глоссарий</h3>
          <Button label="Добавить" icon="pi pi-plus" size="small" text @click="addTerm" />
        </div>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Термин</th>
                <th>Определение</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in form.glossary" :key="index">
                <td><InputText v-model="row.term" class="w-full" /></td>
                <td><Textarea v-model="row.definition" rows="2" class="w-full" autoResize /></td>
                <td class="row-actions">
                  <Button icon="pi pi-trash" text size="small" severity="danger" @click="form.glossary.splice(index, 1)" />
                </td>
              </tr>
              <tr v-if="!form.glossary.length">
                <td colspan="3" class="empty-cell">Терминов нет</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <template #footer>
        <Button label="Отмена" severity="secondary" text @click="editorVisible = false" />
        <Button label="Сохранить" :loading="saving" @click="saveLesson" />
      </template>
    </Dialog>
  </div>
</template>

<style scoped>
.wisdom-page {
  max-width: 1760px;
  width: 100%;
  margin: 0 auto;
  padding: 1.5rem 1rem 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  color: var(--p-text-color, #111827);
}

.wisdom-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.wisdom-header h1 {
  margin: 0 0 0.25rem;
  font-size: 1.7rem;
  font-weight: 700;
  color: var(--p-text-color, #111827);
}

.subtitle {
  margin: 0;
  color: var(--p-text-muted-color, #4b5563);
  font-size: 0.95rem;
}

.wisdom-filters {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 1.1rem;
  border-radius: 12px;
  background: var(--p-surface-card, #ffffff);
  border: 1px solid var(--p-surface-border, #d1d5db);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.filters-main {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-end;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 0 1 16rem;
  min-width: 12rem;
}

.field label {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--p-text-muted-color, #4b5563);
}

.field.grow {
  flex: 1 1 240px;
}

.field :deep(.p-inputtext),
.field :deep(.p-select) {
  width: 100%;
}

.wisdom-table-card {
  padding: 1rem 1.1rem 1.1rem;
  border-radius: 12px;
  background: var(--p-surface-card, #ffffff);
  border: 1px solid var(--p-surface-border, #d1d5db);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.25rem;
}

.table-header h2 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.table-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.gap-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
}

.gap-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0;
  padding: 0.28rem 0.7rem;
  border: 1px solid #cfcfcf;
  border-radius: 999px;
  background: #fafafa;
  color: #333;
  font: inherit;
  font-size: 0.8125rem;
  line-height: 1.2;
  cursor: pointer;
}

.gap-chip:hover {
  border-color: #999;
}

.gap-count {
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}

.gap-chip.active {
  background: #3a3a3a;
  border-color: #3a3a3a;
  color: #fff;
}

.gap-chip.active .gap-count {
  color: #e5e7eb;
}

.wisdom-table :deep(.p-datatable-tbody > tr) {
  cursor: pointer;
}

.wisdom-table :deep(.p-datatable-table-container) {
  overflow-x: auto;
}

.wisdom-table :deep(table) {
  width: 100%;
}

.wisdom-table :deep(.p-datatable-thead > tr > th),
.wisdom-table :deep(.p-datatable-tbody > tr > td) {
  white-space: normal;
  overflow-wrap: anywhere;
}

.wisdom-table :deep(.p-datatable-tbody > tr > td) {
  vertical-align: top;
  border-top: 1px solid var(--p-surface-border, #e5e7eb);
}

.title-cell {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.cover-thumb {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  display: block;
  background: #f3f4f6;
}

.cover-empty,
.empty-cell {
  color: var(--p-text-muted-color, #9ca3af);
  font-size: 0.85rem;
}

.topic-sub {
  color: var(--p-text-muted-color, #6b7280);
  font-size: 0.75rem;
}

.row-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0;
}

.empty-state {
  padding: 1.5rem 1rem;
  text-align: center;
}

.wisdom-paginator {
  margin-top: 0.25rem;
  background: transparent;
  border: 0;
  padding: 0.35rem 0 0;
}

.form-grid {
  display: grid;
  gap: 0.45rem;
}

.cover-edit,
.section-head,
.row-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
}

.section-head {
  justify-content: space-between;
  margin-top: 0.75rem;
}

.section-head h3 {
  margin: 0;
}

.cover-preview {
  width: 160px;
  height: 96px;
  object-fit: cover;
  border-radius: 6px;
  background: #f1f5f9;
}

.file-input {
  display: none;
}

.table-wrap {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th,
td {
  padding: 0.3rem;
  vertical-align: top;
  text-align: left;
}

th {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 600;
}

.w-full {
  width: 100%;
}

@media (max-width: 768px) {
  .wisdom-page {
    padding-inline: 0.75rem;
  }
  .filters-main {
    flex-direction: column;
  }
  .field,
  .field.grow {
    flex: 1 1 auto;
    min-width: 0;
    width: 100%;
  }
}
</style>
